/**
 * @file PreviewToolbar.tsx
 * @description Панель предпросмотра сайта (phase19.16 Р1; uiux-design-r1 §1):
 * экран («Главная ▾» с товаром и страницей на выбор), ширина 360/768/1280
 * (общий `DeviceSwitch` — тот же, что в редакторе страниц), «Черновик |
 * Сейчас на сайте» и «на весь экран».
 */

import { Maximize2 } from "lucide-react";

import { Button } from "../../ui/button";
import { Combobox } from "../../ui/combobox";
import { SelectField } from "../../ui/SelectField";
import { Segmented } from "../Segmented";
import { DeviceSwitch } from "./DeviceSwitch";
import type { PreviewTarget } from "../../../hooks/site-view/usePreviewTargets";
import { PREVIEW_SCREENS, type PreviewMode, type PreviewScreenKey, type PreviewWidth } from "../../../lib/site-view/preview";

interface Props {
  screen: PreviewScreenKey;
  onScreen: (screen: PreviewScreenKey) => void;
  /** Список товаров или страниц для выбранного экрана; `null` — грузится. */
  targets: PreviewTarget[] | null;
  target: string | null;
  onTarget: (slug: string | null) => void;
  widths: readonly PreviewWidth[];
  width: PreviewWidth;
  onWidth: (width: PreviewWidth) => void;
  mode: PreviewMode;
  onMode: ((mode: PreviewMode) => void) | null;
  onFullscreen?: () => void;
}

const SCREEN_OPTIONS = PREVIEW_SCREENS.map((s) => ({ value: s.key, label: s.label }));

export function PreviewToolbar(props: Props) {
  const needsTarget = props.screen === "product" || props.screen === "page";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 max-sm:flex-wrap sm:flex-wrap">
        <SelectField
          value={props.screen}
          onChange={(value) => props.onScreen(value as PreviewScreenKey)}
          options={SCREEN_OPTIONS}
          ariaLabel="Экран предпросмотра"
          className="min-w-0 flex-1 sm:w-48 sm:flex-none"
        />
        {needsTarget ? (
          <Combobox
            className="w-full sm:w-56"
            options={(props.targets ?? []).map((t) => ({ value: t.slug, label: t.label }))}
            value={props.target}
            onChange={props.onTarget}
            placeholder={props.screen === "product" ? "Другой товар…" : "Выберите страницу"}
            emptyText={props.targets === null ? "Загружаем…" : "Ничего не найдено"}
            ariaLabel={props.screen === "product" ? "Товар для предпросмотра" : "Страница для предпросмотра"}
          />
        ) : null}
        <DeviceSwitch devices={props.widths} value={props.width} onChange={props.onWidth} />
      </div>
      {props.onMode || props.onFullscreen ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {props.onMode ? (
            <Segmented
              aria-label="Что показать"
              options={[
                { value: "draft", label: "Черновик" },
                { value: "live", label: "Сейчас на сайте" },
              ]}
              value={props.mode}
              onChange={props.onMode}
            />
          ) : (
            <span />
          )}
          {props.onFullscreen ? (
            <Button variant="ghost" onClick={props.onFullscreen}>
              <Maximize2 aria-hidden />
              Весь экран
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
